import { repositoryResolver } from '@rammetec/data-access';
import { PRODUCT } from '../types/product.js';
import { PRODUCT_UNIT } from '../types/product-unit.js';
import { validateProductUnit } from '../validators/validateProductUnit.js';
import { deriveProductUnitFactorFromProduct } from './deriveProductUnitFactor.js';
import { lifecycleService } from './lifecycleService.js';
import { OrgScopedCrudService } from './OrgScopedCrudService.js';
import { effectiveProductUnitKind, isProductUnitPackaging, } from './productUnitKind.js';
async function loadProduct(orgId, productId) {
    const repo = await repositoryResolver.resolveWithContext(PRODUCT, { orgId });
    return repo.getById(productId);
}
/**
 * Normalize kind defaults and apply derived factor before validate/persist.
 * Measure clears packaging-only fields; packaging clears isDerived.
 */
function prepareProductUnitPayload(data, ctx) {
    const kind = effectiveProductUnitKind(data);
    const extras = (ctx?.extra ?? {});
    const product = extras.product ?? null;
    let next = {
        ...data,
        kind,
    };
    if (kind === 'measure') {
        next = {
            ...next,
            barcode: null,
            standardPackagingId: null,
            isDefault: false,
            isDerived: data.isDerived === true,
        };
        if (next.isDerived === true && product) {
            const derived = deriveProductUnitFactorFromProduct(product, next.unitCode);
            if (derived.ok) {
                // Typed factor is ignored for derived lines (request 0283).
                next = { ...next, factor: derived.factor };
            }
        }
    }
    else {
        next = {
            ...next,
            isDerived: false,
        };
    }
    return next;
}
class ProductUnitService extends OrgScopedCrudService {
    constructor() {
        super({
            collection: 'product-unit',
            entity: PRODUCT_UNIT,
            lifecycle: true,
            validate: validateProductUnit,
            loadValidationContext: (orgId, data, id, ctx) => this.loadValidationExtra(orgId, data, id, ctx),
            sort: (a, b) => {
                if (a.isDefault && !b.isDefault)
                    return -1;
                if (!a.isDefault && b.isDefault)
                    return 1;
                return (a.description ?? '').localeCompare(b.description ?? '');
            },
        });
    }
    /**
     * Fills missing `siblings` / `product` / `excludeId`. Skips IO for keys already
     * on `ctx.extra` (console cache or a prior prepare step — request 0320 rework).
     */
    async loadValidationExtra(orgId, data, id, ctx) {
        const extras = (ctx?.extra ?? {});
        const needSiblings = extras.siblings === undefined;
        const needProduct = extras.product === undefined;
        const needExclude = extras.excludeId === undefined && id !== undefined;
        if (!needSiblings && !needProduct && !needExclude) {
            return undefined;
        }
        const out = {};
        if (needExclude)
            out.excludeId = id;
        const productId = (data.productId ?? '').trim();
        if (needSiblings) {
            if (productId) {
                // Filtered list — not full-org `list()` (import can hit this per row).
                out.siblings = await this.list(orgId, {
                    filterAst: {
                        kind: 'group',
                        combinator: 'and',
                        children: [{ kind: 'condition', field: 'productId', op: 'eq', value: productId }],
                    },
                });
            }
            else {
                out.siblings = [];
            }
        }
        if (needProduct) {
            out.product = productId ? await loadProduct(orgId, productId) : null;
        }
        return out;
    }
    /** Ensure prepare sees product/siblings (same loader; base create skips when filled). */
    async ctxForPrepare(orgId, data, id, ctx) {
        const base = { orgId: ctx?.orgId ?? orgId, ...ctx };
        const loaded = await this.loadValidationExtra(orgId, data, id, base);
        if (!loaded)
            return base;
        return { ...base, extra: { ...loaded, ...base.extra } };
    }
    async create(orgId, data, ctx, opts) {
        const resolvedCtx = await this.ctxForPrepare(orgId, data, undefined, ctx);
        return super.create(orgId, prepareProductUnitPayload(data, resolvedCtx), resolvedCtx, opts);
    }
    async update(orgId, id, data, ctx, opts) {
        // Partial update without `kind` (e.g. an inactivation) must not re-derive the
        // kind: absent would default to packaging and flip a measure line.
        if (data.kind === undefined) {
            return super.update(orgId, id, data, ctx, opts);
        }
        const resolvedCtx = await this.ctxForPrepare(orgId, data, id, ctx);
        return super.update(orgId, id, prepareProductUnitPayload(data, resolvedCtx), resolvedCtx, opts);
    }
    /**
     * Make `productUnitId` the sole active default for its product (0161).
     * Clears `isDefault` on other active packaging siblings, then sets the target.
     */
    async setAsDefault(orgId, productUnitId, ctx) {
        const target = await this.getById(orgId, productUnitId);
        if (!target) {
            throw new Error('Unidade do produto não encontrada.');
        }
        if (!lifecycleService.isActive(target)) {
            throw new Error('Não é possível gravar como padrão uma unidade do produto inativa.');
        }
        if (!isProductUnitPackaging(target)) {
            throw new Error('Somente embalagem pode ser a unidade padrão do produto.');
        }
        let siblings = await this.list(orgId, {
            filterAst: {
                kind: 'group',
                combinator: 'and',
                children: [
                    { kind: 'condition', field: 'productId', op: 'eq', value: target.productId },
                ],
            },
        });
        const extras = (ctx?.extra ?? {});
        const product = extras.product !== undefined
            ? extras.product
            : await loadProduct(orgId, target.productId);
        const baseCtx = {
            ...ctx,
            orgId: ctx?.orgId ?? orgId,
        };
        for (const s of siblings) {
            if (s.id === productUnitId || !s.isDefault || !lifecycleService.isActive(s))
                continue;
            if (!isProductUnitPackaging(s))
                continue;
            await this.update(orgId, s.id, {
                productId: s.productId,
                description: s.description,
                kind: effectiveProductUnitKind(s),
                factor: s.factor,
                isDerived: false,
                quantityDecimals: s.quantityDecimals ?? null,
                unitCode: s.unitCode ?? null,
                barcode: s.barcode ?? null,
                standardPackagingId: s.standardPackagingId ?? null,
                isDefault: false,
                inactivationDate: s.inactivationDate ?? null,
            }, {
                ...baseCtx,
                extra: {
                    siblings: siblings.map((x) => (x.id === s.id ? { ...x, isDefault: false } : x)),
                    product,
                    excludeId: s.id,
                },
            });
        }
        siblings = siblings.map((s) => s.id === productUnitId ? s : { ...s, isDefault: false });
        await this.update(orgId, productUnitId, {
            productId: target.productId,
            description: target.description,
            kind: effectiveProductUnitKind(target),
            factor: target.factor,
            isDerived: false,
            quantityDecimals: target.quantityDecimals ?? null,
            unitCode: target.unitCode ?? null,
            barcode: target.barcode ?? null,
            standardPackagingId: target.standardPackagingId ?? null,
            isDefault: true,
            inactivationDate: target.inactivationDate ?? null,
        }, {
            ...baseCtx,
            extra: {
                siblings: siblings.map((s) => s.id === productUnitId ? { ...s, isDefault: true } : s),
                product,
                excludeId: productUnitId,
            },
        });
        const updated = await this.getById(orgId, productUnitId);
        if (!updated)
            throw new Error('Unidade do produto não encontrada após gravar padrão.');
        return updated;
    }
}
export const productUnitService = new ProductUnitService();
//# sourceMappingURL=productUnitService.js.map